import { __awaiter, __generator } from "tslib";
/*
  Inspired by simpleflakes library: https://www.npmjs.com/package/simpleflakes
  - Full rewrite, simplification
  - Custom epoch
  - Ensuring uniqueness of Flakes generated within the same millisecond
  - Using crypto package for generating random values
  - Base62 encoding that preserves lexicographical order
  - Utility methods for generating and parsing Flakes
  - Generating prefixed Flakes
*/
import BigNum from 'bn.js';
// @ts-expect-error: No typings for library, stable but no activity
import * as base62 from 'base62/lib/custom';
import { FlakePrefixes } from '../global-items/prefixes';
var base62Charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'; // Standard charset for base62 package has lowercase first. JS sorts by uppercase first, so the resulting values would not always be correctly ordered.
base62Charset = base62.indexCharset(base62Charset);
// Encoding parameters
var FLAKE_EPOCH = 1262304000000; // Epoch ms, since Jan 1st, 2010. RUNS OUT IN 2084.
var FLAKE_EPOCH_BIGNUM = new BigNum(FLAKE_EPOCH, 10);
var UNSIGNED_23BIT_MAX = 8388607;
var FLAKE_TIMESTAMP_LENGTH = 41;
var FLAKE_RANDOM_LENGTH = 23;
var FLAKE_RANDOM_SHIFT = 0;
var FLAKE_TIMESTAMP_SHIFT = 23;
var CACHE_BIT_ONES = '1111111111111111111111111111111111111111111111111111111111111111';
/**
 * Flake generation and parsing class. More info: https://sitemate.atlassian.net/wiki/spaces/DP/pages/1925545985/ID+format
 * @class
 */
var Flake = /** @class */ (function () {
    function Flake() {
        this.currentTimestamp = 0;
        this.currentValues = [];
        this.prefixes = FlakePrefixes;
    }
    /**
     * Generate a Flake, optionally with a type and timestamp
     * @param {string} type - The resource type. Can use Flake.prefixes or abbreviated values
     * @param {number=} timestamp - A timestamp in ms from unix epoch. Defaults to current time
     * @return {Promise<string>} A Flake as string
     */
    Flake.prototype.generate = function (type, timestamp) {
        if (timestamp === void 0) { timestamp = new Date().getTime(); }
        return __awaiter(this, void 0, void 0, function () {
            var prefix, flake, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        prefix = this.getPrefix(type);
                        if (!prefix) {
                            throw new Error("Flake type '" + type + "' is not defined.");
                        }
                        _a = prefix + "_";
                        return [4 /*yield*/, this.generateRawFlake(timestamp)];
                    case 1:
                        flake = _a + (_b.sent());
                        if (!this.checkUniqueness(flake, timestamp)) {
                            return [2 /*return*/, this.generate(type, timestamp)];
                        }
                        return [2 /*return*/, flake];
                }
            });
        });
    };
    /**
     * Parse a Flake
     * @param {string} flake - A Flake as string
     * @return {IFlake} The components of the Flake: type, timestamp, random, date
     */
    Flake.prototype.parse = function (flake) {
        var rawFlake = flake;
        var type = undefined;
        // Prefix
        if (flake.includes('_')) {
            var prefix_1 = flake.split('_')[0];
            rawFlake = flake.split('_')[1];
            type =
                Object.keys(FlakePrefixes).find(function (key) { return FlakePrefixes[key] === prefix_1; }) || undefined;
        }
        var parsedFlake = this.parseRawFlake(rawFlake);
        var parsedDate = new Date(parsedFlake.timestamp);
        return {
            type: type,
            timestamp: parsedFlake.timestamp,
            random: parsedFlake.random,
            date: parsedDate,
        };
    };
    Flake.prototype.checkUniqueness = function (flake, timestamp) {
        if (timestamp !== this.currentTimestamp) {
            this.currentTimestamp = timestamp;
            this.currentValues = [];
        }
        if (this.currentValues.includes(flake)) {
            return false;
        }
        this.currentValues.push(flake);
        return true;
    };
    /**
     * Gets a type prefix
     * @param {string} type - The type of the resource, either the full type from the enum, as string (case insensitive), or the abbreviation
     * @return {string} The relevant prefix for the type
     */
    Flake.prototype.getPrefix = function (type) {
        var typeKey = Object.keys(FlakePrefixes).find(function (key) {
            if (key.toLowerCase() === type.toLowerCase()) {
                return true;
            }
            if (FlakePrefixes[key] === type) {
                return true;
            }
            return false;
        });
        if (!typeKey) {
            throw new Error("Flake type '" + type + "' is not defined.");
        }
        var keyIndex = Object.keys(FlakePrefixes).indexOf(typeKey);
        var prefix = Object.values(FlakePrefixes)[keyIndex];
        return prefix || undefined;
    };
    Flake.prototype.generateRawFlake = function (timestamp) {
        if (timestamp === void 0) { timestamp = new Date().getTime(); }
        return __awaiter(this, void 0, void 0, function () {
            var randomNumber, crypto_1, bigInt, base62Encoded;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(typeof process !== 'undefined' && process.versions && process.versions.node)) return [3 /*break*/, 2];
                        return [4 /*yield*/, import('crypto')];
                    case 1:
                        crypto_1 = _a.sent();
                        randomNumber = crypto_1.randomInt(0, UNSIGNED_23BIT_MAX);
                        return [3 /*break*/, 3];
                    case 2:
                        randomNumber = Math.floor(Math.random() * UNSIGNED_23BIT_MAX);
                        _a.label = 3;
                    case 3:
                        bigInt = new BigNum(timestamp - FLAKE_EPOCH, 10)
                            .shln(FLAKE_TIMESTAMP_SHIFT)
                            .add(new BigNum(randomNumber, 10));
                        base62Encoded = base62.encode(Number(bigInt.toString()), base62Charset);
                        return [2 /*return*/, base62Encoded];
                }
            });
        });
    };
    Flake.prototype.parseRawFlake = function (rawFlake) {
        var base10Decoded = base62.decode(rawFlake, base62Charset);
        var bigInt = new BigNum(String(base10Decoded), 10);
        var timestampBits = bigInt
            .shrn(FLAKE_TIMESTAMP_SHIFT)
            .and(new BigNum(CACHE_BIT_ONES.substr(0, FLAKE_TIMESTAMP_LENGTH), 2));
        var timestamp = timestampBits.add(FLAKE_EPOCH_BIGNUM).toString(10);
        var randomBits = bigInt
            .shrn(FLAKE_RANDOM_SHIFT)
            .and(new BigNum(CACHE_BIT_ONES.substr(0, FLAKE_RANDOM_LENGTH), 2));
        var random = randomBits.toString(36);
        return {
            timestamp: Number(timestamp),
            random: random,
        };
    };
    return Flake;
}());
export default new Flake();
